import { SYSTEM_FOLDER_REDACT_TRASH } from '@redact-modules/trash/constants';
import { isNil } from 'lodash';
import moment from 'moment';
import type { IntlShape } from 'react-intl';

// Retention days stamped on a trashed item at delete time (TDO details tag or
// folder SDO data). Items trashed before the rename carry the legacy
// `numberOfDaysToKeep` key instead of `trashRetentionDays`, so read both —
// new key wins. Returns undefined when neither key is present.
// TODO: the legacy fallback only exists so staging behaves correctly for items
// already in its trash (the feature is not in production yet) — drop the
// `numberOfDaysToKeep` read once those items have cycled out.
export const getStampedTrashRetentionDays = (
  stamp: unknown
): number | undefined => {
  const read = (key: string): unknown =>
    typeof stamp === 'object' && stamp !== null && key in stamp
      ? (stamp as Record<string, unknown>)[key]
      : undefined;
  const raw = read('trashRetentionDays') ?? read('numberOfDaysToKeep');
  return isNil(raw) ? undefined : Number(raw);
};

export const isDateEligibleToDelete = (
  givenDate: string,
  numberOfDays: number
) => {
  const today = moment(new Date());
  const actualDate = moment(givenDate);
  const diffenceDays = today.diff(actualDate, 'days');
  return diffenceDays >= numberOfDays;
};

export const getDaysUntilDeletion = (
  givenDate: string,
  trashRetentionDays: number
) => {
  const today = moment(new Date());
  const movedToTrashDateTime = moment(givenDate);
  return trashRetentionDays - today.diff(movedToTrashDateTime, 'days');
};

// Number of days remaining below which the countdown adds the trailing hours
// ("2 days, 23 hours"). At or above it, only a coarse whole-day count is shown.
// Set to 3 so the hours detail appears exactly in the range the urgency color
// starts warning (amber at <= 3 days).
const HOURS_DETAIL_UNDER_DAYS = 3;

// Human-readable time until permanent deletion, in three bands:
//   • >= HOURS_DETAIL_UNDER_DAYS out: a single whole-day count, rounded to the
//     *nearest* day ("14 days"). Nearest (not floor) keeps a freshly-trashed
//     item from reading one less than its retention; nearest (not ceil) keeps
//     the handoff to the hours band smooth — just above the cutoff rounds to the
//     same day the "N days, M hours" band shows just below it, instead of
//     jumping a full day. The exact count here is cosmetic anyway: the urgency
//     color only warns at <= 3 days, well inside the hours band.
//   • under that window but >= 1 day: floored days plus the remaining hours
//     ("2 days, 23 hours"), so the countdown isn't understated near the deadline.
//   • under a day: hours ("3 hours" / "1 hour"), then "< 1 hour" for anything
//     less (including already-due items, purged on next load).
// Wording/plurals are localized (en/fr), the caller passes its `intl`.
export const formatTimeUntilDeletion = (
  givenDate: string,
  trashRetentionDays: number,
  intl: IntlShape
): string => {
  const deletionAt = moment(givenDate).add(trashRetentionDays, 'days');

  // Guard malformed/missing inputs so the column shows the standard empty value rather than "NaN".
  if (!deletionAt.isValid() || !givenDate) {
    return '--';
  }

  const minutesLeft = deletionAt.diff(moment(new Date()), 'minutes');
  if (minutesLeft < 60) {
    return intl.formatMessage({
      id: 'timeUntilDeletionLessThanHour',
      defaultMessage: '< 1 hour',
    });
  }

  const hoursLeft = Math.floor(minutesLeft / 60);
  if (hoursLeft < 24) {
    return intl.formatMessage(
      {
        id: 'timeUntilDeletionHours',
        defaultMessage: '{hours, plural, one {# hour} other {# hours}}',
      },
      { hours: hoursLeft }
    );
  }

  const days = Math.floor(hoursLeft / 24);
  const hours = hoursLeft % 24;
  const daysPart = (value: number) =>
    intl.formatMessage(
      {
        id: 'timeUntilDeletionDays',
        defaultMessage: '{days, plural, one {# day} other {# days}}',
      },
      { days: value }
    );

  // Beyond the detail window, show one coarse day count rounded to the nearest
  // whole day (see the header note on why nearest, not floor or ceil).
  if (days >= HOURS_DETAIL_UNDER_DAYS) {
    return daysPart(Math.round(hoursLeft / 24));
  }

  // Inside the window, a whole number of days needs no trailing hours.
  if (hours === 0) {
    return daysPart(days);
  }

  const hoursPart = intl.formatMessage(
    {
      id: 'timeUntilDeletionHours',
      defaultMessage: '{hours, plural, one {# hour} other {# hours}}',
    },
    { hours }
  );

  return intl.formatMessage(
    {
      id: 'timeUntilDeletionDaysHours',
      defaultMessage: '{daysPart}, {hoursPart}',
    },
    { daysPart: daysPart(days), hoursPart }
  );
};

export const formatExportDateTime = (
  value?: string | number | null
): string => {
  if (isNil(value) || value === '') {
    return '';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }
  return date.toISOString();
};

interface ExportTdoVeritoneFile {
  fileType?: string;
  mimetype?: string;
}

interface ExportTdoDetails {
  veritoneFile?: ExportTdoVeritoneFile;
}

// Check fileType AND mimetype independently rather than `??`-falling back:
// a truthy-but-non-MIME placeholder in fileType (e.g. "unknown") would
// otherwise mask the real mimetype and mis-classify image/PDF TDOs.
const candidateExportTypes = (details?: ExportTdoDetails): string[] =>
  [details?.veritoneFile?.fileType, details?.veritoneFile?.mimetype].filter(
    (t): t is string => typeof t === 'string' && t.length > 0
  );

export const isExportImageTdo = (details?: ExportTdoDetails): boolean =>
  candidateExportTypes(details).some((t) => t.includes('image'));

export const isExportPdfTdo = (details?: ExportTdoDetails): boolean =>
  candidateExportTypes(details).some((t) => t.includes('pdf'));

export const findDuration = (
  startDateTime: string,
  stopDateTime: string
): string => {
  let output = '';

  const rawDuration = moment.duration(moment(stopDateTime).diff(startDateTime));
  const hours = Math.floor(rawDuration.asHours());
  rawDuration.subtract(hours, 'h');
  if (hours > 0) {
    output += `${hours}:`;
  }
  const minutes = Math.floor(rawDuration.asMinutes());
  rawDuration.subtract(minutes, 'm');
  if (minutes < 10 && hours > 0) {
    output += `0${minutes}:`;
  } else {
    output += `${minutes}:`;
  }
  const seconds = Math.floor(rawDuration.asSeconds());
  if (seconds < 10) {
    output += `0${seconds}`;
  } else {
    output += seconds.toString();
  }

  return output;
};

export const removeKeyQuotes = (value: string) =>
  value?.replace(/"([^"]+)":/g, '$1:');

export interface FolderPathItem {
  id: string;
  name: string;
  parent: {
    id: string;
    name?: string;
  } | null;
}

// TODO: The folderPath returned from the backend is not in the expected order.
// Need to sort it before displaying it.
// This is a temporary fix and should be removed once the issue is resolved on the backend.
export const sortFolderPath = (path: FolderPathItem[]): FolderPathItem[] => {
  if (!path.length) {
    return [];
  }

  const idMap = new Map<string, FolderPathItem>();
  const parentToChild = new Map<string, FolderPathItem>();

  // Build maps
  for (const folder of path) {
    idMap.set(folder.id, folder);

    if (folder.parent?.id) {
      parentToChild.set(folder.parent.id, folder);
    }
  }

  // Find root
  const root = path.find((f) => f.parent === null);
  if (!root) {
    return [];
  }

  const result: FolderPathItem[] = [];
  let current: FolderPathItem | undefined = root;

  while (current) {
    result.push(current);

    const next = parentToChild.get(current.id);

    // If there's a next but it's not in original list → missing node
    if (next && !idMap.has(next.id)) {
      return [];
    }

    current = next;
  }

  // If not all nodes are used → missing link or broken chain
  if (result.length !== path.length) {
    return [];
  }

  return result;
};

// --- Folders v2 compatibility (VE-21114) -----------------------------------
// v2 changes `folderPath`, gated per-org by `v2FoldersEnabled` (both shapes
// coexist at runtime). These helpers normalise back to the v1 shape so existing
// depth thresholds and positional indexing keep working.

// Folder queried directly: v2 excludes the folder itself → path is 1 shorter;
// add 1 to recover the v1-equivalent depth. Typed on `.length` only (callers
// pass folderPath-shaped arrays of varying element type).
export const effectiveFolderDepth = (
  path: { readonly length: number } | undefined | null,
  isV2Folders: boolean
): number => (path?.length ?? 0) + (isV2Folders ? 1 : 0);

// A TDO's folder entry (`tdo.folders[n]`): v1 includes the folder itself as the
// leaf of `folderPath`; v2 excludes it (leaf missing). Re-attach it — with a
// parent link to the deepest ancestor so `sortFolderPath` stays connected — to
// rebuild the full v1-equivalent chain the export/trash consumers expect.
export const tdoFolderChain = (
  folder:
    | {
        id?: string | null;
        name?: string | null;
        folderPath?: FolderPathItem[] | null;
      }
    | undefined
    | null,
  isV2Folders: boolean
): FolderPathItem[] => {
  const path = folder?.folderPath ?? [];
  // No-op under v1, when we can't identify the leaf (no id), or if the leaf is
  // already present (guards against double-adding).
  if (
    !isV2Folders ||
    !folder?.id ||
    path.some((node) => node.id === folder.id)
  ) {
    return path;
  }
  const sorted = sortFolderPath(path);
  const immediateParent = sorted[sorted.length - 1] ?? null;
  return [
    ...path,
    {
      id: folder.id,
      name: folder.name ?? '',
      parent: immediateParent
        ? { id: immediateParent.id, name: immediateParent.name }
        : null,
    },
  ];
};

/**
 * Is this TDO filed in Trash? Takes the whole `tdo.folders` array.
 *
 * Checks each entry's OWN name as well as its `folderPath`. Under v2 a folder's
 * `folderPath` excludes the folder itself, so a file sitting directly in Trash
 * has "REDACT TRASH" as a leaf that appears in no path at all — the name check
 * is the only thing that catches it. The path check still covers v1, and the
 * case where Trash is an ancestor rather than the immediate parent.
 *
 * Every entry, not just `folders[0]`: a file can be filed under more than one
 * folder (an incomplete move leaves it in both its original folder and Trash),
 * and the Trash entry is not guaranteed to come first.
 */
export type TdoFolderMembership = ReadonlyArray<{
  readonly id?: string | null;
  readonly name?: string | null;
  readonly folderPath?: ReadonlyArray<{
    readonly id?: string | null;
    readonly name?: string | null;
  } | null> | null;
} | null>;

export const isTdoFiledInTrash = (
  folders: TdoFolderMembership | undefined | null
): boolean =>
  !!folders?.some(
    (folder) =>
      folder?.name === SYSTEM_FOLDER_REDACT_TRASH ||
      !!folder?.folderPath?.some(
        (ancestor) => ancestor?.name === SYSTEM_FOLDER_REDACT_TRASH
      )
  );

// Resolve the identifier a folder/case OLP mutation must authorize by. Under
// Folders v2 the platform gates these mutations (delete/move/rename/link) on the
// folder id; the deprecated treeObjectId returns `not_allowed`. Under v1 — or
// under v2 when no folder id is available — fall back to the treeObjectId. This
// centralises the rule that was previously re-inlined (and drifting) at every
// mutation call site: `isV2 ? id : treeObjectId` vs `isV2 && id ? id : ...`.
export function resolveFolderMutationId<F, T>(
  isV2Folders: boolean | undefined,
  ids: { readonly folderId?: F | null; readonly treeObjectId: T }
): F | T {
  return isV2Folders && ids.folderId ? ids.folderId : ids.treeObjectId;
}

interface RootFolderChildCounts {
  readonly childFolders?: {
    readonly count?: number | null;
    readonly records?: readonly unknown[] | null;
  } | null;
}

// How many child folders a root folder appears to hold, read from whatever
// `childFolders` page the caller's query already fetched. Deliberately no
// dedicated count query — limitations of that, accepted for now:
//   - `FolderList.count` is the records returned in THAT page, not an org-wide
//     total, so the value is capped by the query's limit (30 for the dashboard's
//     listRootFolders fetch). Two roots that both fill a page tie.
//   - Where the page is filtered the signal is filtered too: search compares
//     name-matching children, and the trash query compares "already has a
//     REDACT TRASH folder" (1 vs 0) — which happens to point at the real root.
// Either way a tie falls through to the first owner-less root, i.e. the
// pre-flag behaviour, so the worst case is no better than today, never worse.
const rootFolderChildCount = (folder: unknown): number => {
  const { childFolders } = (folder ?? {}) as RootFolderChildCounts;
  return Math.max(
    typeof childFolders?.count === 'number' ? childFolders.count : 0,
    childFolders?.records?.length ?? 0
  );
};

// Resolve an org's "admin" root folder: the owner-less root (a user-owned root
// can sort first for some orgs), else the first root. Centralises the choice so
// case/folder creation, Trash, and the legacy dashboard resolve the same root
// and can't drift apart. Generic over the caller's root-folder shape; returns
// that same element type (with its childFolders etc. intact).
//
// `smartSelection` (app-config flag `rootFolderSmartSelection`) handles the org
// that returns MORE THAN ONE owner-less root — the platform can leave extra,
// effectively empty roots behind, and taking the first one strands the org's
// content. With the flag on, the owner-less root holding the most child folders
// wins; ties (including "no child data in this payload") keep the first
// owner-less root, i.e. the pre-flag behaviour. Every call site must pass the
// same flag value, otherwise the dashboard, search, and Trash could each land
// on a different root.
export const selectAdminRootFolder = <T extends { ownerId?: string | null }>(
  rootFolders: readonly T[] | undefined | null,
  options?: { readonly smartSelection?: boolean }
): T | undefined => {
  const ownerLessRoots =
    rootFolders?.filter((folder) => isNil(folder.ownerId)) ?? [];
  const firstMatch = ownerLessRoots[0] ?? rootFolders?.[0];

  if (!options?.smartSelection || ownerLessRoots.length < 2) {
    return firstMatch;
  }

  return ownerLessRoots.reduce((best, folder) =>
    rootFolderChildCount(folder) > rootFolderChildCount(best) ? folder : best
  );
};
